import * as zod from 'zod';

// Import from ./types because we don't want to pull in non-Node dependencies.
// E.g. if we pull in `fs`, Remote Control will not compile.
import { InstallationInfo } from '@sb/firmware-info/types';
import { TargetFirmwareInfo } from '@sb/local-build-data/types';

import { RobotOperationMode } from './robotIdentity';

export const SoftwareUpdateData = zod.object({
  activeBuildID: zod.string(),
  activeBuildName: zod.string(),
  activeFirmwareInfo: zod.array(
    InstallationInfo.extend({
      jointNumber: zod.number(),
    }),
  ),
  activeOperationMode: RobotOperationMode.optional(),
  targetBuildID: zod.string(),
  targetBuildName: zod.string(),
  targetFirmwareInfo: TargetFirmwareInfo,
  targetOperationMode: RobotOperationMode.optional(),
  status: zod.enum([
    'pending',
    'downloading',
    'downloaded',
    'installing',
    'installed',
  ]),
  downloadProgress: zod.string().optional(),
  // Indicates a software installation request that has been written by feathers-bot
  // but not yet read (and cleared) by updater-bot
  isSoftwareInstallRequested: zod.boolean(),
  // Set by the UI (in a `create` call) to ask updater-bot to abort an in-flight
  // image download. feathers-bot relays this to updater-bot over the messenger
  // (UpdaterBotConnector.CancelDownload). Write-only input; never on a `find`.
  cancelDownloadRequested: zod.boolean().optional(),
  // Set by the UI (in a `create` call) when the operator clicks "Use USB
  // Update" from the offline panel while an online download is blocking it.
  // feathers-bot cancels the online download AND persists the offline
  // selection (selected_offline_build.json) so the online target can't
  // re-adopt the slot until the USB build installs or the drive is removed.
  // Sent alongside `targetBuildID` (the online build to cancel) and
  // `offlineUpdatePath` (the USB image). Write-only input; never on a `find`.
  switchToOfflineRequested: zod.boolean().optional(),
  lastErrorAt: zod.string().optional(),
  lastErrorMessage: zod.string().optional(),
  lastWarningNotified: zod.boolean().optional(),
  lastWarningMessage: zod.string().optional(),
  strategy: zod.string(),
  updatedAt: zod.string(),
  // when running a software install from disk, this contains the url to the directory
  // containing all the images
  offlineUpdatePath: zod.string().optional(),
  // Sub-phase of an offline (USB) install while status === 'downloading'.
  // 'copying' — botctl is copying the tar from USB to /tmp.
  // 'loading' — docker load is consuming the copied tar.
  // Undefined when no offline install is active or the phase is unknown.
  // Used by the UI to distinguish "Copying…" from "Loading…" — without
  // this in the wire-format schema, zod.safeParse on the frontend strips
  // the field even when feathers-bot includes it in the response.
  offlineLoadPhase: zod.enum(['copying', 'loading']).optional(),
  // Number of consecutive failed offline install attempts. Updater-bot
  // auto-retries until it hits its cap; after that, a manual re-request is
  // required. The UI uses this to render retry progress / exhausted state.
  offlineInstallFailureCount: zod.number().int().nonnegative().optional(),
  // Derived boolean: true when the failure count has reached updater-bot's
  // auto-retry cap. UI uses this to re-enable the install button (so the
  // user can manually retry) and to shift messaging from "retrying" to
  // "install failed".
  isOfflineInstallRetryExhausted: zod.boolean().optional(),
  // Network interface used for downloading (e.g. 'eth0', 'wlan0')
  downloadNetworkInterface: zod.string().optional(),
});

export type SoftwareUpdateData = zod.infer<typeof SoftwareUpdateData>;
